import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@app/router/routes'
import { MapContainer, ImageOverlay, Polygon, Marker, Tooltip, useMap, Pane } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Maximize2, X } from 'lucide-react'
import { useLanguage } from '@shared/contexts/LanguageContext'
import { useRotatedMapDrag } from '@shared/hooks/useRotatedMapDrag'
import ZoneDetailMap from './ZoneDetailMap'

const ZONE_ORDER = ['entrance', 'zone1', 'zone2', 'zone3', 'zone4']

// map_2.png 原始像素尺寸
const IMG_W = 1781
const IMG_H = 860

// Leaflet 座標是 [y, x]（lat, lng），y 軸從圖片底部往上算，
// 所以要把偵測到的像素座標（原點在左上）轉成 Leaflet 的 [IMG_H - y, x]
const toLatLng = (x, y) => [IMG_H - y, x]
const boxToPolygon = (x1, y1, x2, y2) => [
  toLatLng(x1, y1),
  toLatLng(x2, y1),
  toLatLng(x2, y2),
  toLatLng(x1, y2),
]

// 展區標籤統一色（入口／展區一～四共用），法水長流象限另外使用獨立的識別色
const ZONE_LABEL_COLOR = '#2F6FA3'
const ZONE2_QUADRANT_COLOR = '#44842d'

// 五個展區熱區座標（依新版 map_2.png 版面重新劃分；從舊圖 4000x2250 等比例換算過來的近似值，
// 之後如有更精準的新圖座標可再微調）
const ZONES = [
  { id: 'entrance', box: [1304, 372, 1532, 577], color: ZONE_LABEL_COLOR },
  { id: 'zone1', box: [1279, 586, 1656, 805], color: ZONE_LABEL_COLOR },
  { id: 'zone2', box: [477, 201, 1248, 633], color: ZONE_LABEL_COLOR },
  { id: 'zone3', box: [194, 639, 564, 832], color: ZONE_LABEL_COLOR },
  { id: 'zone4', box: [70, 264, 428, 617], color: ZONE_LABEL_COLOR },
]

// map_2.png 上「弘法・現況」烤入標籤的獨立熱區，跟 zone2 大範圍熱區分開，
// 點擊行為跟展區二熱區一樣：navigate 到展區二介紹頁
const HONGFA_BOX = [709, 414, 993, 506]
const HONGFA_CENTER = [(HONGFA_BOX[0] + HONGFA_BOX[2]) / 2, (HONGFA_BOX[1] + HONGFA_BOX[3]) / 2]

// 展區二內部四象限標籤（左上長／右上水／左下流／右下法），純視覺標示，不觸發點擊事件
// const ZONE2_BOX = ZONES.find(z => z.id === 'zone2').box
const ZONE2_BOX = [237, 35, 1958, 1149]
const [ZONE2_X1, ZONE2_Y1, ZONE2_X2, ZONE2_Y2] = ZONE2_BOX
const ZONE2_MID_X = (ZONE2_X1 + ZONE2_X2) / 2
const ZONE2_MID_Y = (ZONE2_Y1 + ZONE2_Y2) / 1.5

const ZONE2_COLOR = ZONE2_QUADRANT_COLOR

// 各分區旗幟標籤的錨點座標（map_2.png 原始像素座標；從舊圖等比例換算過來的近似值），
// 跟熱區 box 分開獨立控制，只影響標籤顯示位置，不影響可點擊範圍
const ZONE_LABEL_POINT = {
  entrance: [1405, 416],
  zone1: [1494, 730],
  zone2: [832, 433],
  zone3: [346, 750],
  zone4: [205, 534],
}
const ZONE_LABEL_LATLNG = Object.fromEntries(
  Object.entries(ZONE_LABEL_POINT).map(([id, [x, y]]) => [id, toLatLng(x, y)])
)

const ZONE2_QUADRANTS = [
  { id: 'chang', label: '長', x: 1120, y: 730 },
  { id: 'shui', label: '水', x: 3140, y: 730 },
  { id: 'liu', label: '流', x: 1120, y: 1560 },
  { id: 'fa', label: '法', x: 3140, y: 1560 },
].map(q => ({ ...q, center: toLatLng(q.x, q.y) }))

// 純文字標籤用的透明零尺寸圖示，只借 Marker 定位，實際顯示交給 Tooltip
const EMPTY_ICON = L.divIcon({ className: '', html: '', iconSize: [0, 0] })

const IMAGE_BOUNDS = [[0, 0], [IMG_H, IMG_W]]

function ZoneInfoModal({ zone, onClose, t, zone2RestoreState }) {
  if (!zone) return null

  if (zone.id === 'zone2') {
    return <ZoneDetailMap onClose={onClose} initialRestoreState={zone2RestoreState} />
  }

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm p-6 relative bg-white rounded-[20px] shadow-xl flex flex-col gap-3 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label={t('exhibition-map.closeAria')}
          className="w-6 h-6 absolute right-6 top-6 overflow-hidden cursor-pointer flex-shrink-0 hover:opacity-60 transition-opacity"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="9.25" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" />
            <path d="M7 7L13 13M13 7L7 13" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        <div className="pr-8">
          <p className="text-[#BC313D] text-xl font-bold font-['Noto_Serif_TC'] leading-7">
            {t(`exhibition-map.${zone.id}.title`)}
          </p>
          <p className="text-[#6E625B] text-sm font-['Noto_Sans_TC'] mt-1">
            {t(`exhibition-map.${zone.id}.subtitle`)}
          </p>
        </div>
        <p className="text-[#3D3A36] text-base font-['Noto_Sans_TC'] leading-6 whitespace-pre-line">
          {t(`exhibition-map.${zone.id}.desc`)}
        </p>
      </div>
    </div>
  )
}

// 容器尺寸變化時（開全螢幕、裝置旋轉、視窗縮放）要通知 Leaflet 重新量測，
// 否則地圖內部座標系統會停留在舊尺寸，造成點擊熱區座標對不上畫面
function MapResizeHandler() {
  const map = useMap()

  useEffect(() => {
    const container = map.getContainer()
    const invalidate = () => map.invalidateSize()

    const observer = new ResizeObserver(() => invalidate())
    observer.observe(container)

    window.addEventListener('orientationchange', invalidate)
    return () => {
      observer.disconnect()
      window.removeEventListener('orientationchange', invalidate)
    }
  }, [map])

  return null
}

// CSS 強制橫向旋轉時，修正 Leaflet 拖曳方向跟視覺滑動方向對不上的問題
function RotatedDragHandler({ enabled }) {
  const map = useMap()
  useRotatedMapDrag(map, { enabled })
  return null
}

// 點了展區按鈕之後，把地圖飛到該區域並置中、稍微放大
function FlyToZone({ zone }) {
  const map = useMap()

  useEffect(() => {
    if (!zone) return
    const positions = boxToPolygon(...zone.box)
    const bounds = L.latLngBounds(positions)
    map.flyToBounds(bounds, { padding: [24, 24], duration: 0.6 })
  }, [zone, map])

  return null
}

// 手機版容器又窄又矮，fitBounds 塞滿容器會裁掉圖片上下或左右；
// 縮小 zoomOffset 級距讓初始畫面比「剛好塞滿」再縮小一點，露出完整地圖四周
// offset 改變（例如裝置旋轉切換直向／橫向的預設值）時，重新對整張圖 fitBounds 算出乾淨的基準 zoom 再套用 offset，
// 不沿用前一次（可能是另一個方向、或使用者手動縮放過）的 zoom 值，避免疊加錯誤
function InitialZoomOffset({ offset }) {
  const map = useMap()

  useEffect(() => {
    // 直橫向切換時容器尺寸已經變了，但 Leaflet 內部快取的 _size 可能還沒更新
    // （負責更新的 MapResizeHandler 是靠 ResizeObserver 非同步觸發，時機比這裡晚），
    // fitBounds 前要先同步呼叫 invalidateSize 強制重新量測容器，否則算出來的基準 zoom 會沿用切換前的舊尺寸
    map.invalidateSize()
    map.fitBounds(IMAGE_BOUNDS, { padding: [0, 0], animate: false })
    if (!offset) return
    map.setZoom(map.getZoom() + offset, { animate: false })
  }, [map, offset])

  return null
}

// MapContainer 的 minZoom prop 只在掛載當下生效，之後裝置旋轉切換直橫向不會自動更新縮放下限，
// 改用 map.setMinZoom() 動態套用，讓直橫向各自有獨立的 zoom out 下限
function DynamicMinZoom({ minZoom }) {
  const map = useMap()

  useEffect(() => {
    if (minZoom == null) return
    map.setMinZoom(minZoom)
  }, [map, minZoom])

  return null
}

// 地圖本體（一般卡片模式、全螢幕層都會用到，用 key 讓兩邊各自持有獨立的 Leaflet 實例）
function ExhibitionMapCanvas({ onZoneClick, activeZoneId = null, onHoverChange, focusZone = null, showTargetMarker = false, initialZoomOffset = 0, minZoom = null, isPublished = true, forceLandscape = false, t }) {
  const [hoveredZoneId, setHoveredZoneId] = useState(null)
  const navigate = useNavigate()
  const isDisabled = !isPublished

  const polygons = useMemo(() => ZONES.map(zone => ({
    ...zone,
    positions: boxToPolygon(...zone.box),
  })), [])

  const handleHover = (id) => {
    setHoveredZoneId(id)
    onHoverChange?.(id)
  }

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <MapContainer
        crs={L.CRS.Simple}
        bounds={IMAGE_BOUNDS}
        boundsOptions={{ padding: [0, 0] }}
        maxBounds={IMAGE_BOUNDS}
        maxBoundsViscosity={1.0}
        minZoom={-2}
        maxZoom={2}
        zoomSnap={0.25}
        zoomDelta={0.5}
        zoomControl={false}
        attributionControl={false}
        preferCanvas
        style={{ height: '100%', width: '100%', background: 'transparent' }}
      >
        <MapResizeHandler />
        <RotatedDragHandler enabled={forceLandscape} />
        <FlyToZone zone={focusZone} />
        <DynamicMinZoom minZoom={minZoom} />
        <InitialZoomOffset offset={initialZoomOffset} />
        <ImageOverlay url="/image/transmission/map_2.png" bounds={IMAGE_BOUNDS} />
        {polygons.map(zone => {
          return (
            <Polygon
              key={zone.id}
              positions={zone.positions}
              pathOptions={{
                color: zone.color,
                weight: 0,
                fillColor: zone.color,
                fillOpacity: 0,
                interactive: !isDisabled,
              }}
              eventHandlers={isDisabled ? {} : {
                click: () => {
                  handleHover(null)
                  if (zone.id === 'zone2') {
                    onZoneClick(zone)
                  } else {
                    const idx = ZONE_ORDER.indexOf(zone.id)
                    if (idx !== -1) {
                      navigate(ROUTES.TRANSMISSION_AREA(idx), {
                        state: { mapRestore: { from: 'fullscreen', activeZoneId: zone.id } },
                      })
                    }
                  }
                },
                mouseover: () => handleHover(zone.id),
                mouseout: () => handleHover(null),
              }}
            />
          )
        })}
        {showTargetMarker && focusZone && (
          <Marker
            position={focusZone.id === 'zone2' ? toLatLng(...HONGFA_CENTER) : ZONE_LABEL_LATLNG[focusZone.id]}
            icon={L.divIcon({
              className: '',
              html: `<svg class="table-target-marker" width="30" height="30" viewBox="0 0 24 24" fill="#BC313D" style="display: block;">
                <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7.14 11.34 7.45 11.6a.85.85 0 0 0 1.1 0C12.86 21.34 20 15.25 20 10c0-4.42-3.58-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
              </svg>`,
              iconSize: [30, 30],
              iconAnchor: [15, 30],
            })}
            interactive={false}
          />
        )}
      </MapContainer>
    </div>
  )
}

// 非全螢幕卡片檢視：純圖片打滿寬度，不可縮放/拖曳，只保留熱區點擊（用絕對定位色塊疊在圖片上，
// 座標用百分比換算，響應圖片實際顯示尺寸）
function StaticMapCanvas({ onZoneClick, activeZoneId = null, onHoverChange, isPublished = true, onLockedZoneClick, t, showTargetMarker = false, targetZoneId = null }) {
  const [hoveredZoneId, setHoveredZoneId] = useState(null)
  const navigate = useNavigate()

  const handleHover = (id) => {
    setHoveredZoneId(id)
    onHoverChange?.(id)
  }

  const isDisabled = !isPublished

  return (
    <div className="relative w-full" style={{ aspectRatio: `${IMG_W} / ${IMG_H}` }}>
      <img
        src="/image/transmission/map_2.png"
        alt=""
        className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
        draggable={false}
      />
      {ZONES.map(zone => {
        const [x1, y1, x2, y2] = zone.box
        const isDisabled = !isPublished
        return (
          <button
            key={zone.id}
            type="button"
            disabled={isDisabled && isPublished}
            onClick={() => {
              if (!isPublished) {
                onLockedZoneClick?.()
                return
              }
              handleHover(null)
              if (zone.id === 'zone2') {
                onZoneClick(zone)
              } else {
                const idx = ZONE_ORDER.indexOf(zone.id)
                if (idx !== -1) {
                  navigate(ROUTES.TRANSMISSION_AREA(idx), {
                    state: { mapRestore: { from: 'static', activeZoneId: zone.id } },
                  })
                }
              }
            }}
            onMouseEnter={() => !isDisabled && handleHover(zone.id)}
            onMouseLeave={() => !isDisabled && handleHover(null)}
            aria-label={t(`exhibition-map.${zone.id}.shortTitle`)}
            className={`absolute bg-transparent flex items-center justify-center ${isDisabled ? (isPublished ? 'cursor-not-allowed' : 'cursor-pointer') : 'cursor-pointer'}`}
            style={{
              left: `${(x1 / IMG_W) * 100}%`,
              top: `${(y1 / IMG_H) * 100}%`,
              width: `${((x2 - x1) / IMG_W) * 100}%`,
              height: `${((y2 - y1) / IMG_H) * 100}%`,
            }}
          />
        )
      })}
      {ZONES.map(zone => {
        const [px, py] = ZONE_LABEL_POINT[zone.id]
        const isTarget = showTargetMarker && targetZoneId === zone.id
        if (!isTarget) return null
        return (
          <div
            key={`pin-${zone.id}`}
            className="absolute pointer-events-none select-none z-[1001]"
            style={{
              left: `${(px / IMG_W) * 100}%`,
              top: `${(py / IMG_H) * 100}%`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <svg className="table-target-marker" width="30" height="30" viewBox="0 0 24 24" fill="#BC313D" style={{ display: 'block' }}>
              <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7.14 11.34 7.45 11.6a.85.85 0 0 0 1.1 0C12.86 21.34 20 15.25 20 10c0-4.42-3.58-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
            </svg>
          </div>
        )
      })}
      {/* {ZONE2_QUADRANTS.map(q => (
        <span
          key={q.id}
          className="zone-label-tooltip pointer-events-none absolute -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${(q.x / IMG_W) * 100}%`,
            top: `${(q.y / IMG_H) * 100}%`,
            '--zone-label-color': ZONE2_COLOR,
          }}
        >
          <span className="zone-balloon">
            <span className="zone-balloon__bubble">{q.label}</span>
            <span className="zone-balloon__tail" />
          </span>
        </span>
      ))} */}
    </div>
  )
}

// 桌面版：hover 熱區時，容器上方置中浮現半透明展區名稱
function HoverLabel({ zoneId, t }) {
  if (!zoneId) return null
  return (
    <div className="absolute inset-x-0 top-3 z-[900] flex justify-center pointer-events-none">
      <span
        className="px-5 py-2 rounded-full text-white text-lg font-bold font-['Noto_Serif_TC']"
        style={{ background: 'rgba(61,58,54,0.55)', backdropFilter: 'blur(2px)' }}
      >
        {t(`exhibition-map.${zoneId}.title`)}
      </span>
    </div>
  )
}

// 手機版：展區按鈕列，點擊後讓地圖飛到該區域並高亮；再點一次已啟用的按鈕可取消選取
function ZoneButtonBar({ activeZoneId, onSelectZone, t }) {
  return (
    <div className="flex gap-1.5 overflow-x-auto px-1 py-2">
      {ZONE_ORDER.map(id => {
        const isActive = activeZoneId === id
        return (
          <button
            key={id}
            onClick={() => onSelectZone(id)}
            className="flex-shrink-0 px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap cursor-pointer transition-colors"
            style={{
              background: isActive ? '#BC313D' : 'rgba(255,255,255,0.9)',
              color: isActive ? '#fff' : '#3D3A36',
              border: isActive ? '1px solid #BC313D' : '1px solid rgba(144,118,84,0.3)',
              boxShadow: isActive ? '0 2px 6px rgba(188,49,61,0.25)' : '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            {t(`exhibition-map.${id}.shortTitle`)}
          </button>
        )
      })}
    </div>
  )
}

// 手機直向時，把全螢幕層強制用 CSS 轉 90 度顯示成橫版（不依賴裝置實際轉動，
// 也不呼叫 Fullscreen API／orientation.lock，純視覺旋轉，所有裝置都有效）
function usePortraitOrientation() {
  const [isPortrait, setIsPortrait] = useState(() => window.matchMedia('(orientation: portrait)').matches)

  useEffect(() => {
    const mql = window.matchMedia('(orientation: portrait)')
    const handler = (e) => setIsPortrait(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return isPortrait
}

// 全螢幕地圖初始 zoom 級距，依裝置目前是直向還是橫向分開設定，方便個別微調
const FULLSCREEN_ZOOM_OFFSET_PORTRAIT = 0.1
const FULLSCREEN_ZOOM_OFFSET_LANDSCAPE = 0

// 全螢幕地圖 zoom out 下限，直橫向容器寬高比不同，fitBounds 算出的基準 zoom 也不同，
// 共用同一個 minZoom 會導致兩邊「縮到底」的視覺效果不一致，改成分開設定
const FULLSCREEN_MIN_ZOOM_PORTRAIT = -2
const FULLSCREEN_MIN_ZOOM_LANDSCAPE = -1

// 全螢幕層：純 CSS fixed 疊層蓋滿整個 viewport，不依賴瀏覽器 Fullscreen API
// （iOS Safari 對一般元素的 requestFullscreen() 不支援，這個做法所有裝置都保證有效）
function FullscreenLayer({ onClose, onZoneClick, t, isTouchDevice, activeZoneId, focusZone, showTargetMarker, onHoverChange, hoveredZoneId, onSelectZone }) {
  // 強制橫向旋轉功能先關閉，恢復成原本正常方向；未來若要復原，取消下方兩行註解即可
  // const isPortrait = usePortraitOrientation()
  // const forceLandscape = isTouchDevice && isPortrait
  const forceLandscape = false
  const isPortrait = usePortraitOrientation()
  const initialZoomOffset = isPortrait ? FULLSCREEN_ZOOM_OFFSET_PORTRAIT : FULLSCREEN_ZOOM_OFFSET_LANDSCAPE
  const minZoom = isPortrait ? FULLSCREEN_MIN_ZOOM_PORTRAIT : FULLSCREEN_MIN_ZOOM_LANDSCAPE

  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prevOverflow }
  }, [])

  return (
    <div
      className="fixed z-[2000]"
      style={
        forceLandscape
          ? {
            top: 0,
            left: '100%',
            right: 'auto',
            bottom: 'auto',
            height: '100dvw',
            width: '100dvh',
            background: '#2A2A2A',
            transform: 'rotate(90deg)',
            transformOrigin: 'top left',
          }
          : { top: 0, left: 0, right: 0, bottom: 0, height: '100dvh', width: '100dvw', background: '#2A2A2A' }
      }
    >
      <ExhibitionMapCanvas
        onZoneClick={onZoneClick}
        activeZoneId={activeZoneId}
        onHoverChange={onHoverChange}
        focusZone={focusZone}
        showTargetMarker={showTargetMarker}
        initialZoomOffset={initialZoomOffset}
        minZoom={minZoom}
        forceLandscape={forceLandscape}
        t={t}
      />
      {!isTouchDevice && <HoverLabel zoneId={hoveredZoneId} t={t} />}
      <button
        onClick={onClose}
        aria-label={t('exhibition-map.closeAria')}
        className="absolute right-4 top-4 z-[2100] w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
        style={{ top: 'max(1rem, env(safe-area-inset-top))', right: 'max(1rem, env(safe-area-inset-right))' }}
      >
        <X size={20} className="text-[#3D3A36]" />
      </button>
    </div>
  )
}

// 熱區尚未開放時點擊的提醒視窗：告知使用者開放時間，而不是完全沒反應
function ComingSoonModal({ open, onClose, publishedTime, t }) {
  if (!open) return null

  const dateStr = publishedTime
    ? new Intl.DateTimeFormat('zh-TW', {
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(publishedTime))
    : ''

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm p-6 relative bg-white rounded-[20px] shadow-xl flex flex-col items-center gap-3 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label={t('exhibition-map.closeAria')}
          className="w-6 h-6 absolute right-6 top-6 overflow-hidden cursor-pointer flex-shrink-0 hover:opacity-60 transition-opacity"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="9.25" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" />
            <path d="M7 7L13 13M13 7L7 13" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        <p className="text-[#BC313D] text-xl font-bold font-['Noto_Serif_TC'] leading-7 mt-2">
          {t('exhibition-map.comingSoon.title')}
        </p>
        <p className="text-[#3D3A36] text-base font-['Noto_Sans_TC'] leading-6">
          {dateStr
            ? t('exhibition-map.comingSoon.desc').replace('{{date}}', dateStr)
            : t('exhibition-map.comingSoon.descNoDate')}
        </p>
      </div>
    </div>
  )
}

function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(() => window.matchMedia('(pointer: coarse)').matches)

  useEffect(() => {
    const mql = window.matchMedia('(pointer: coarse)')
    const handler = (e) => setIsTouch(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return isTouch
}

export default function ExhibitionMap({ initialRestoreState = null, publishedTime = null }) {
  const { t } = useLanguage()
  const navigate = useNavigate()
  // 展區資訊公開時間到之前，互動地圖只能看第一層、所有熱區都不能點、也不開放全螢幕放大；
  // 沒有 publishedTime（尚未取得 API 回應）時預設當作尚未公開
  const isPublished = !!publishedTime && Date.now() >= new Date(publishedTime).getTime()
  // zone2（法水長流細部地圖）在報導頁時是全螢幕疊層，回上一頁要直接重新打開
  const [selectedZone, setSelectedZone] = useState(() =>
    initialRestoreState?.zone2 ? ZONES.find(z => z.id === 'zone2') ?? null : null
  )
  // 使用者從全螢幕地圖上「法／水／長／流」四象限標籤點進來時，記住點的是哪個子分區，
  // 讓 ZoneDetailMap 開啟時直接飛到並高亮對應分區按鈕
  const [selectedSubZone, setSelectedSubZone] = useState(null)
  // initialRestoreState?.zone2（報導頁桌位返回時的定位/紅點資料）只能在「掛載後第一次打開」ZoneDetailMap 時套用一次；
  // 使用者關掉細部地圖後若重新打開，不該再拿同一筆舊資料重新定位、重新顯示紅點
  const hasConsumedZone2RestoreRef = useRef(false)
  const [isFullscreen, setIsFullscreen] = useState(() => !!initialRestoreState?.isFullscreen)
  const [hoveredZoneId, setHoveredZoneId] = useState(null)
  // activeZoneId 只代表「使用者手動選取的分區」（手機版分區按鈕高亮 + 第一層地圖熱區紅框），
  // 從報導頁還原時只需要全螢幕地圖飛到 focusZone，不應該連帶把背景第一層地圖的分區也鎖成選取狀態
  const [activeZoneId, setActiveZoneId] = useState(null)
  const [focusZone, setFocusZone] = useState(() =>
    initialRestoreState?.activeZoneId ? ZONES.find(z => z.id === initialRestoreState.activeZoneId) ?? null : null
  )
  // 紅點跳動指示（table-target-marker）只在「剛從展區介紹頁定位回來、且不會直接開細部地圖」時顯示一次；
  // 使用者自己手動點展區熱區展開全螢幕（handleSelectZone）只是想看該展區，不該連帶顯示紅點；
  // 從報導頁桌位返回時 initialRestoreState 同時帶 zone2，代表要直接重新打開 ZoneDetailMap（見上面 selectedZone），
  // 這種情況第一層背景地圖不該額外顯示紅點，否則關掉細部地圖後，第一層會露出不該存在的紅點跳動。
  // 只讀取掛載當下的 initialRestoreState，之後不再被手動點擊觸發
  const [showTargetMarker, setShowTargetMarker] = useState(() => !!initialRestoreState?.activeZoneId && !initialRestoreState?.zone2 && !initialRestoreState?.noTargetMarker)
  const [showComingSoon, setShowComingSoon] = useState(false)
  const isTouchDevice = useIsTouchDevice()

  const handleSelectZone = (id) => {
    setShowTargetMarker(false)
    if (activeZoneId === id) {
      setActiveZoneId(null)
      setFocusZone(null)
      return
    }
    setActiveZoneId(id)
    setFocusZone(ZONES.find(z => z.id === id))
  }

  return (
    <div className="w-full">
      {!isFullscreen && (
        <div className="relative w-full" style={{ isolation: 'isolate' }}>
          <StaticMapCanvas
            onZoneClick={(zone) => {
              // 第一層小地圖點展區二熱區，直接打開 ZoneDetailMap 細部地圖，跟其他展區「點了就直接進去」一致；
              // 其他展區熱區只負責展開全螢幕，維持預設 zoom 顯示完整地圖，不設定 focusZone，
              // 避免展開後自動飛到/放大到剛才點的那個展區；使用者主動點熱區展開，不是從報導頁定位回來，不該顯示紅點
              setShowTargetMarker(false)
              if (zone.id === 'zone2') {
                setSelectedSubZone(null)
                setSelectedZone(zone)
              } else {
                setIsFullscreen(true)
              }
            }}
            onHoverChange={setHoveredZoneId}
            activeZoneId={activeZoneId}
            isPublished={isPublished}
            onLockedZoneClick={() => setShowComingSoon(true)}
            t={t}
            showTargetMarker={showTargetMarker}
            targetZoneId={initialRestoreState?.activeZoneId}
          />
          {!isTouchDevice && <HoverLabel zoneId={hoveredZoneId} t={t} />}

          {isPublished && (
            <button
              onClick={() => {
                setShowTargetMarker(false)
                setIsFullscreen(true)
              }}
              aria-label={t('exhibition-map.fullscreenAria')}
              className="absolute right-3 bottom-3 z-[1000] w-9 h-9 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
            >
              <Maximize2 size={18} className="text-[#3D3A36]" />
            </button>
          )}
        </div>
      )}

      {isFullscreen && (
        <FullscreenLayer
          onClose={() => {
            // 使用者關掉全螢幕地圖代表已經看過紅點定位提示了，重新展開不該再顯示
            setShowTargetMarker(false)
            setIsFullscreen(false)
          }}
          onZoneClick={(zone, subZoneId = null) => {
            if (zone.id === 'zone2') {
              setSelectedSubZone(subZoneId)
              setSelectedZone(zone)
            } else {
              const idx = ZONE_ORDER.indexOf(zone.id)
              if (idx !== -1) {
                navigate(ROUTES.TRANSMISSION_AREA(idx), {
                  state: { mapRestore: { from: 'fullscreen', activeZoneId: zone.id } },
                })
              }
            }
          }}
          t={t}
          isTouchDevice={isTouchDevice}
          activeZoneId={activeZoneId}
          focusZone={focusZone}
          showTargetMarker={showTargetMarker}
          hoveredZoneId={hoveredZoneId}
          onHoverChange={setHoveredZoneId}
          onSelectZone={handleSelectZone}
        />
      )}

      <ZoneInfoModal
        zone={selectedZone}
        onClose={() => {
          setSelectedZone(null)
          setSelectedSubZone(null)
          hasConsumedZone2RestoreRef.current = true
        }}
        t={t}
        zone2RestoreState={
          selectedSubZone
            ? { activeZone: selectedSubZone, selectedTableId: null }
            : (hasConsumedZone2RestoreRef.current ? null : (initialRestoreState?.zone2 ?? null))
        }
      />

      <ComingSoonModal
        open={showComingSoon}
        onClose={() => setShowComingSoon(false)}
        publishedTime={publishedTime}
        t={t}
      />
    </div>
  )
}
